import axiosInstance from "../api/axiosInstance";
import { getFirebaseMessaging } from "../firebase";
import { getToken, onMessage } from "firebase/messaging";
import toast from "react-hot-toast";

const VAPID_KEY =
    "BH2s8Cgg7wIWJbss46sSYn0FnWleAqQ9kVKIj4I4nwLEi_RVksH1IU54paKRpYk0vRjSrTqH2bYAgi2w6MmTw0s";
    // "BGlKuT2wItwtWmLAvdBMR8xo8T3d5MLICfwgualCB_pGs5KZd16fbC3quKcFQUdctto75anK83DJ8RDsTBYxxnM";

const FIREBASE_MESSAGING_SW_PATH = "/firebase-messaging-sw.js";

let tokenRequestPromise = null;
let foregroundMessageCleanup = null;

const isNotificationSupported = () =>
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator;

const getNotificationUrl = (payload) =>
    payload?.fcmOptions?.link ||
    payload?.notification?.click_action ||
    payload?.data?.link ||
    payload?.data?.click_action ||
    payload?.data?.url ||
    "";

const getForegroundNotificationContent = (payload) => {
    const data = payload?.data || {};

    return {
        title:
            payload?.notification?.title ||
            data.title ||
            data.notificationTitle ||
            data.notification_title ||
            data.subject ||
            "Notification",
        body:
            payload?.notification?.body ||
            data.body ||
            data.message ||
            data.serMsg ||
            data.notificationBody ||
            data.notification_body ||
            "",
        icon:
            payload?.notification?.icon ||
            data.icon ||
            data.image ||
            "/logo192.png",
        url: getNotificationUrl(payload),
    };
};

const dispatchForegroundNotification = (notification) => {
    window.dispatchEvent(
        new CustomEvent("firebase-foreground-message", {
            detail: notification,
        })
    );
};

const showForegroundSystemNotification = async (title, options) => {
    if (!isNotificationSupported() || Notification.permission !== "granted") {
        return;
    }

    try {
        const registration = await registerFirebaseMessagingServiceWorker();

        if (registration?.showNotification) {
            registration.showNotification(title, options);
            return;
        }

        new Notification(title, options);
    } catch (err) {
        console.error("Foreground notification error", err);
    }
};

export const registerFirebaseMessagingServiceWorker = async () => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
        return null;
    }

    try {
        return await navigator.serviceWorker.register(FIREBASE_MESSAGING_SW_PATH);
    } catch (err) {
        console.error("Firebase SW error:", err);
        return null;
    }
};

export const requestNotificationPermission = async (userId, retry = false) => {
    if (!userId || !isNotificationSupported()) {
        return null;
    }

    if (tokenRequestPromise) {
        return tokenRequestPromise;
    }

    tokenRequestPromise = requestNotificationPermissionInternal(userId, retry);

    try {
        return await tokenRequestPromise;
    } finally {
        tokenRequestPromise = null;
    }
};

const requestNotificationPermissionInternal = async (userId, retry = false) => {
    try {
        const permission =
            Notification.permission === "default"
                ? await Notification.requestPermission()
                : Notification.permission;

        if (permission !== "granted") {
            console.log("Notification permission denied");
            return null;
        }

        const registration = await registerFirebaseMessagingServiceWorker();
        const messaging = await getFirebaseMessaging();

        if (!registration || !messaging) {
            return null;
        }

        const token = await getToken(messaging, {
            vapidKey: VAPID_KEY,
            serviceWorkerRegistration: registration,
        });

        if (!token) {
            return null;
        }

        console.log("FCM Token:", token);
        const payload = { fcmToken: token, platform: "web" };
        const response = await axiosInstance.post(`/users/save-fcm-token`, payload);

        if (response.status >= 200 && response.status < 300) {
            console.log(response.data.data, "responsetoken");
        }

        return token;
    } catch (err) {
        console.error("FCM error", err);

        if (!retry) {
            console.log("Retrying FCM token request once...");
            return requestNotificationPermissionInternal(userId, true);
        }

        console.log("FCM token request failed after retry");
        return null;
    }
};

export const listenForegroundMessages = async () => {
    if (foregroundMessageCleanup) {
        return foregroundMessageCleanup;
    }

    const messaging = await getFirebaseMessaging();

    if (!messaging) {
        return null;
    }

    const unsubscribe = onMessage(messaging, (payload) => {
        console.log("Foreground Message:", payload);

        const notification = getForegroundNotificationContent(payload);
        const { title, body, icon, url } = notification;

        dispatchForegroundNotification(notification);
        toast(body ? `${title}: ${body}` : title, { duration: 6000 });

        showForegroundSystemNotification(title, {
            body,
            icon,
            data: { url },
        });
    });

    foregroundMessageCleanup = () => {
        if (foregroundMessageCleanup) {
            unsubscribe();
            foregroundMessageCleanup = null;
        }
    };

    return foregroundMessageCleanup;
};
